/**
 * Frequency Matcher Utility
 *
 * Provides functions to check if flights operate on specific dates based on IATA SSIM frequency patterns.
 *
 * Frequency Format:
 * - Uses digits 1-7 to represent days of the week
 * - 1 = Monday, 2 = Tuesday, ..., 7 = Sunday
 * - Example: "135" = Mon/Wed/Fri, "1234567" = Daily
 */

/**
 * Maps IATA day numbers to JavaScript Date day numbers
 * IATA: 1=Mon, 2=Tue, ..., 7=Sun
 * JS Date: 0=Sun, 1=Mon, ..., 6=Sat
 */
const IATA_TO_JS_DAY: Record<string, number> = {
  '1': 1, // Monday
  '2': 2, // Tuesday
  '3': 3, // Wednesday
  '4': 4, // Thursday
  '5': 5, // Friday
  '6': 6, // Saturday
  '7': 0, // Sunday
};

/**
 * Maps JavaScript Date day numbers to IATA day numbers
 */
const JS_TO_IATA_DAY: Record<number, string> = {
  0: '7', // Sunday
  1: '1', // Monday
  2: '2', // Tuesday
  3: '3', // Wednesday
  4: '4', // Thursday
  5: '5', // Friday
  6: '6', // Saturday
};

/**
 * Day names for human-readable output
 */
const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const FULL_DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

/**
 * Checks if a flight operates on a specific date based on its frequency pattern
 *
 * @param targetDate - The date to check
 * @param frequency - IATA frequency pattern (e.g., "135" for Mon/Wed/Fri)
 * @param scheduleStart - Flight schedule start date
 * @param scheduleEnd - Flight schedule end date
 * @returns true if the flight operates on the target date, false otherwise
 *
 * @example
 * // Check if a Mon/Wed/Fri flight operates on December 25, 2026 (Friday)
 * doesDateMatchFrequency(
 *   new Date('2026-12-25'),
 *   '135',
 *   new Date('2026-01-01'),
 *   new Date('2026-12-31')
 * ); // Returns: true
 */
export function doesDateMatchFrequency(
  targetDate: Date,
  frequency: string,
  scheduleStart: Date,
  scheduleEnd: Date
): boolean {
  // Normalize dates to midnight for accurate comparison
  const normalizedTarget = new Date(targetDate);
  normalizedTarget.setHours(0, 0, 0, 0);

  const normalizedStart = new Date(scheduleStart);
  normalizedStart.setHours(0, 0, 0, 0);

  const normalizedEnd = new Date(scheduleEnd);
  normalizedEnd.setHours(0, 0, 0, 0);

  // Check if date is within schedule period
  if (normalizedTarget < normalizedStart || normalizedTarget > normalizedEnd) {
    return false;
  }

  // Get the day of week for the target date (0=Sun, 6=Sat in JS)
  const dayOfWeek = normalizedTarget.getDay();

  // Convert to IATA day number
  const iataDay = JS_TO_IATA_DAY[dayOfWeek];

  // Check if this day is in the frequency pattern
  return frequency.includes(iataDay);
}

/**
 * Gets all dates within a range when a flight operates based on its frequency pattern
 *
 * @param frequency - IATA frequency pattern (e.g., "135" for Mon/Wed/Fri)
 * @param rangeStart - Start of the date range to check
 * @param rangeEnd - End of the date range to check
 * @param scheduleStart - Flight schedule start date
 * @param scheduleEnd - Flight schedule end date
 * @returns Array of dates when the flight operates
 *
 * @example
 * // Get all dates a Mon/Wed/Fri flight operates in January 2026
 * getOperatingDates(
 *   '135',
 *   new Date('2026-01-01'),
 *   new Date('2026-01-31'),
 *   new Date('2026-01-01'),
 *   new Date('2026-12-31')
 * ); // Returns: [Jan 5 (Mon), Jan 7 (Wed), Jan 9 (Fri), ...]
 */
export function getOperatingDates(
  frequency: string,
  rangeStart: Date,
  rangeEnd: Date,
  scheduleStart: Date,
  scheduleEnd: Date
): Date[] {
  const operatingDates: Date[] = [];

  // Normalize all dates to midnight
  const normalizedRangeStart = new Date(Math.max(
    new Date(rangeStart).setHours(0, 0, 0, 0),
    new Date(scheduleStart).setHours(0, 0, 0, 0)
  ));

  const normalizedRangeEnd = new Date(Math.min(
    new Date(rangeEnd).setHours(0, 0, 0, 0),
    new Date(scheduleEnd).setHours(0, 0, 0, 0)
  ));

  // If there's no overlap between range and schedule, return empty array
  if (normalizedRangeStart > normalizedRangeEnd) {
    return operatingDates;
  }

  // Convert frequency pattern to JS day numbers for faster lookup
  const operatingDays = new Set(
    frequency.split('').map(iataDay => IATA_TO_JS_DAY[iataDay])
  );

  // Iterate through each day in the range
  const currentDate = new Date(normalizedRangeStart);
  while (currentDate <= normalizedRangeEnd) {
    // Check if this day of week is in the operating pattern
    if (operatingDays.has(currentDate.getDay())) {
      operatingDates.push(new Date(currentDate));
    }

    // Move to next day
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return operatingDates;
}

/**
 * Converts frequency pattern to human-readable description
 *
 * @param frequency - IATA frequency pattern (e.g., "135" for Mon/Wed/Fri)
 * @param includeCount - If true, includes the number of days per week
 * @returns Human-readable description
 *
 * @example
 * getFrequencyDescription('1234567'); // Returns: "Daily"
 * getFrequencyDescription('12345');   // Returns: "Weekdays (Mon-Fri)"
 * getFrequencyDescription('135');     // Returns: "Mon, Wed, Fri"
 * getFrequencyDescription('135', true); // Returns: "Mon, Wed, Fri (3 days/week)"
 */
export function getFrequencyDescription(frequency: string, includeCount: boolean = false): string {
  if (!frequency || frequency.length === 0) {
    return 'No days selected';
  }

  // Sort the frequency pattern for consistent comparison
  const sortedFreq = frequency.split('').sort().join('');

  // Special cases
  if (sortedFreq === '1234567') {
    return includeCount ? 'Daily (7 days/week)' : 'Daily';
  }
  if (sortedFreq === '12345') {
    return includeCount ? 'Weekdays (Mon-Fri, 5 days/week)' : 'Weekdays (Mon-Fri)';
  }
  if (sortedFreq === '67') {
    return includeCount ? 'Weekends (Sat-Sun, 2 days/week)' : 'Weekends (Sat-Sun)';
  }

  // Custom pattern - list the days
  const days = frequency
    .split('')
    .map(iataDay => {
      const jsDay = IATA_TO_JS_DAY[iataDay];
      return DAY_NAMES[jsDay === 0 ? 6 : jsDay - 1]; // Adjust for Mon=0 in DAY_NAMES
    })
    .sort((a, b) => {
      // Sort by weekday order (Mon first, Sun last)
      const order = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      return order.indexOf(a) - order.indexOf(b);
    });

  const description = days.join(', ');

  if (includeCount) {
    const count = frequency.length;
    const plural = count === 1 ? 'day' : 'days';
    return `${description} (${count} ${plural}/week)`;
  }

  return description;
}

/**
 * Gets the full day name for a given date
 *
 * @param date - The date to get the day name for
 * @returns Full day name (e.g., "Monday")
 */
export function getDayName(date: Date): string {
  const dayOfWeek = date.getDay();
  return FULL_DAY_NAMES[dayOfWeek === 0 ? 6 : dayOfWeek - 1];
}

/**
 * Validates if a frequency pattern is valid according to IATA SSIM rules
 *
 * @param frequency - The frequency pattern to validate
 * @returns Object with isValid flag and optional error message
 *
 * @example
 * validateFrequencyPattern('135');     // { isValid: true }
 * validateFrequencyPattern('111');     // { isValid: false, error: "Duplicate days not allowed" }
 * validateFrequencyPattern('');        // { isValid: false, error: "At least one day required" }
 */
export function validateFrequencyPattern(frequency: string): {
  isValid: boolean;
  error?: string;
  duplicates?: string[];
} {
  // Rule 1: Required
  if (!frequency || frequency.trim().length === 0) {
    return {
      isValid: false,
      error: 'At least one day must be selected',
    };
  }

  // Rule 2: Only digits 1-7
  const validPattern = /^[1-7]+$/;
  if (!validPattern.test(frequency)) {
    return {
      isValid: false,
      error: 'Frequency must contain only digits 1-7 (1=Mon, 7=Sun)',
    };
  }

  // Rule 3: No duplicates
  const days = frequency.split('');
  const uniqueDays = new Set(days);
  if (uniqueDays.size !== days.length) {
    // Find which days are duplicated
    const duplicates = days.filter((day, index) => days.indexOf(day) !== index);
    const uniqueDuplicates = Array.from(new Set(duplicates));

    return {
      isValid: false,
      error: `Frequency contains duplicate days: ${uniqueDuplicates.map(d => `"${d}"`).join(', ')}. Each day can only appear once.`,
      duplicates: uniqueDuplicates,
    };
  }

  // Rule 4: Maximum 7 days
  if (frequency.length > 7) {
    return {
      isValid: false,
      error: 'Maximum 7 days allowed (one week)',
    };
  }

  return { isValid: true };
}

/**
 * Counts the number of operating days in a given date range
 *
 * @param frequency - IATA frequency pattern
 * @param rangeStart - Start of the date range
 * @param rangeEnd - End of the date range
 * @param scheduleStart - Flight schedule start date
 * @param scheduleEnd - Flight schedule end date
 * @returns Number of days the flight operates in the range
 */
export function countOperatingDays(
  frequency: string,
  rangeStart: Date,
  rangeEnd: Date,
  scheduleStart: Date,
  scheduleEnd: Date
): number {
  return getOperatingDates(frequency, rangeStart, rangeEnd, scheduleStart, scheduleEnd).length;
}

/**
 * Checks if two frequency patterns have any overlapping days
 *
 * @param freq1 - First frequency pattern
 * @param freq2 - Second frequency pattern
 * @returns true if there are overlapping days
 *
 * @example
 * hasFrequencyOverlap('135', '246'); // false (no overlap)
 * hasFrequencyOverlap('135', '127'); // true (day 1 overlaps)
 */
export function hasFrequencyOverlap(freq1: string, freq2: string): boolean {
  const days1 = new Set(freq1.split(''));
  const days2 = new Set(freq2.split(''));

  for (const day of Array.from(days1)) {
    if (days2.has(day)) {
      return true;
    }
  }

  return false;
}

/**
 * Gets the overlapping days between two frequency patterns
 *
 * @param freq1 - First frequency pattern
 * @param freq2 - Second frequency pattern
 * @returns Array of overlapping IATA day codes
 *
 * @example
 * getFrequencyOverlap('135', '127'); // ['1'] (Monday overlaps)
 * getFrequencyOverlap('1234567', '135'); // ['1', '3', '5']
 */
export function getFrequencyOverlap(freq1: string, freq2: string): string[] {
  const days1 = new Set(freq1.split(''));
  const days2 = new Set(freq2.split(''));
  const overlap: string[] = [];

  for (const day of Array.from(days1)) {
    if (days2.has(day)) {
      overlap.push(day);
    }
  }

  return overlap.sort();
}

/**
 * Calculates a 30-day preview window for displaying operating dates.
 * If the schedule is entirely in the past, starts from schedule start.
 * Otherwise starts from today (clamped to schedule bounds).
 */
export function getPreviewDateRange(
  startDate: string,
  endDate: string
): { rangeStart: Date; rangeEnd: Date; scheduleStart: Date; scheduleEnd: Date } {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const scheduleStart = new Date(startDate);
  scheduleStart.setHours(0, 0, 0, 0);

  const scheduleEnd = new Date(endDate);
  scheduleEnd.setHours(0, 0, 0, 0);

  const rangeStart = scheduleEnd < today
    ? scheduleStart
    : (today > scheduleStart ? today : scheduleStart);

  const thirtyDaysLater = new Date(rangeStart);
  thirtyDaysLater.setDate(thirtyDaysLater.getDate() + 30);
  const rangeEnd = thirtyDaysLater < scheduleEnd ? thirtyDaysLater : scheduleEnd;

  return { rangeStart, rangeEnd, scheduleStart, scheduleEnd };
}
